import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  getRedirectResult,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signOut,
  type User
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { auth } from "./firebase";
import { db } from "./firebase";

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: () => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (email: string, password: string, nickname: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getRedirectResult(auth)
      .then(async (result) => {
        if (result?.user) {
          await ensureUserProfile(result.user);
        }
      })
      .catch((err: unknown) => {
        setError(readAuthError(err));
      });

    return onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      setLoading(false);
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      error,
      login: async () => {
        setError(null);
        try {
          await signInWithRedirect(auth, new GoogleAuthProvider());
        } catch (err: unknown) {
          setError(readAuthError(err));
        }
      },
      loginWithEmail: async (email: string, password: string) => {
        setError(null);
        try {
          const result = await signInWithEmailAndPassword(auth, email, password);
          await ensureUserProfile(result.user);
        } catch (err: unknown) {
          setError(readAuthError(err));
          throw err;
        }
      },
      registerWithEmail: async (email: string, password: string, nickname: string) => {
        setError(null);
        try {
          const result = await createUserWithEmailAndPassword(auth, email, password);
          await ensureUserProfile(result.user, nickname);
        } catch (err: unknown) {
          setError(readAuthError(err));
          throw err;
        }
      },
      logout: async () => {
        await signOut(auth);
      }
    }),
    [user, loading, error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function readAuthError(err: unknown) {
  if (err && typeof err === "object" && "code" in err) {
    const code = String((err as { code: unknown }).code);
    if (code === "auth/unauthorized-domain") {
      return "Firebase Auth blocked this domain. Try http://localhost:5173 instead of http://127.0.0.1:5173, and add your production domain in Firebase Auth authorized domains.";
    }
    if ("message" in err) return `${code}: ${String((err as { message: unknown }).message)}`;
    return code;
  }
  return err instanceof Error ? err.message : "Sign in failed.";
}

async function ensureUserProfile(user: User, nickname?: string) {
  const userRef = doc(db, "users", user.uid);
  const snapshot = await getDoc(userRef);
  if (snapshot.exists()) return;
  await setDoc(userRef, {
    uid: user.uid,
    email: user.email || "",
    nickname: nickname || user.displayName || "",
    profilePictureUrl: user.photoURL || "",
    role: "USER",
    managedSections: [],
    coins: 0,
    purchasedItems: [],
    displayedItems: [],
    following: [],
    followers: [],
    blockedUsers: [],
    collectedPosts: [],
    hideAds: false
  });
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
